import { useState } from "react";
import { AtSign, CheckCircle2, Loader2, Mail } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";

/**
 * Shared "Change sign-in email" block for dashboard settings pages.
 *
 * Under the hood we call supabase.auth.updateUser({ email }). Supabase
 * Auth responds by sending confirmation emails to BOTH the current
 * email and the new one - the account only moves once the user clicks
 * the link in the new-address email. Until then, sign-in still uses
 * the old address. The email itself is rendered with our branded
 * `change-email` template.
 *
 * UI is intentionally defensive: we confirm before sending, show
 * clear "pending confirmation" state, and surface any API error.
 */
export function ChangeEmailSection() {
  const { user } = useAuth();
  const [newEmail, setNewEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return toast.error("That doesn't look like a valid email.");
    }
    if (trimmed === user?.email?.toLowerCase()) {
      return toast.error("That's already your email.");
    }

    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ email: trimmed });
    setSubmitting(false);

    if (error) {
      return toast.error(error.message);
    }
    setPending(trimmed);
    setNewEmail("");
    toast.success("Confirmation emails sent.");
  }

  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="mb-1 flex items-center gap-2">
        <AtSign className="h-4 w-4 text-volt" />
        <h2 className="text-sm font-bold uppercase tracking-wider">
          Change sign-in email
        </h2>
      </div>
      <p className="mb-5 text-sm text-muted-foreground">
        Currently signed in as{" "}
        <span className="font-semibold text-foreground">{user?.email}</span>.
        You'll need to confirm the switch from both the old and new inboxes
        before it takes effect.
      </p>

      {pending ? (
        <div className="rounded-xl border border-volt/30 bg-volt-subtle p-4">
          <div className="mb-2 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-volt" />
            <p className="text-sm font-semibold">Confirmation emails sent</p>
          </div>
          <p className="text-xs text-muted-foreground">
            We sent links to{" "}
            <span className="font-medium text-foreground">{user?.email}</span>{" "}
            and{" "}
            <span className="font-medium text-foreground">{pending}</span>.
            Click the link in the new address to complete the change.
          </p>
          <button
            type="button"
            onClick={() => setPending(null)}
            className="mt-3 text-xs font-semibold text-volt underline underline-offset-2 hover:text-foreground"
          >
            Start over
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="email"
              placeholder="new.email@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={submitting}
              required
              className="pl-9"
            />
          </div>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Sending…
              </>
            ) : (
              <>Send confirmation</>
            )}
          </Button>
        </form>
      )}
    </section>
  );
}
